import {match} from 'ts-pattern';

import {useCallback, useEffect, useState} from 'react';

import {isNil, mergeAll} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {
  DmsDataGrid,
  WAREHOUSE_GRID_CODES,
  catchUnhandledDataGridActions,
  logError,
  useWarehouseDataGridTreeFolder,
} from '@dms/shared';
import {FullscreenDataGridWrapper} from '@dms/warehouse/shared-components';

import {suffixTestId, TestIdProps} from 'shared/web';

import type {ActionCallback, Filters, QueryFilterObject} from 'features/eag-grid';

import {MaterialPurchaseListTabExternalFilters} from './MaterialPurchaseListTabExternalFilters';

const SUPPLIER_ORDER_PURCHASE_LIST_FILTER_ID = 'supplier-order-purchase-list-filter';

interface MaterialPurchaseListTabProps extends TestIdProps {
  warehouseId: string;
  onAddMaterialFromPurchaseListToBasket: (requestItemId: string[]) => Promise<void>;
  onActiveFolderIdChange?: (activeFolderId?: string) => void;
  onFilterChange: (filters: Filters) => void;
}

export const MaterialPurchaseListTab = (props: MaterialPurchaseListTabProps) => {
  // T20-58899 -The bulk selector does not reset after the DG refresh, so we need to reset the entire component using a random key.
  const [randomString, setRandomString] = useState<string>('');
  const [folderTreeRef, dataGridModifier, activeFolderId] = useWarehouseDataGridTreeFolder();

  useEffect(() => {
    props.onActiveFolderIdChange?.(activeFolderId);
  }, [activeFolderId]);

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => {
      const modifiedFilter = mergeAll([filter, {warehouseId: props.warehouseId}]);

      return dataGridModifier ? dataGridModifier?.queryModifier(modifiedFilter) : modifiedFilter;
    },
    [props.warehouseId, dataGridModifier]
  );

  const warehouseActionCallback: ActionCallback = async ({actionKey, rowId}) => {
    const ids = isArray(rowId) ? rowId : [rowId];

    if (isNil(ids)) {
      return logError('Selected ids are not defined');
    }

    await match(actionKey)
      .with(
        'addToBasket',
        async () =>
          await props.onAddMaterialFromPurchaseListToBasket(ids).then(() => {
            setRandomString(String(Math.random()));
          })
      )
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <FullscreenDataGridWrapper
      externalFilterId={SUPPLIER_ORDER_PURCHASE_LIST_FILTER_ID}
      folderTreeRef={folderTreeRef}
      hideCardWrapper
      data-testid={props['data-testid']}
      key={randomString}
    >
      <DmsDataGrid
        // DG must be re-rendered on folder change to update the query modifier
        // eslint-disable-next-line no-restricted-syntax
        key={dataGridModifier?.key}
        gridCode={WAREHOUSE_GRID_CODES.warehouseSupplierOrderPurchaseListCart}
        actionCallback={warehouseActionCallback}
        externalFilterId={SUPPLIER_ORDER_PURCHASE_LIST_FILTER_ID}
        queryModifier={queryModifier}
        _useAsLastResort_definitionBehaviorOverrides={{filterMode: 'NATIVE'}}
        data-testid={suffixTestId('warehouseSupplierOrderPurchaseListBasket', props)}
        filterComponent={MaterialPurchaseListTabExternalFilters}
        onFilterChanged={props.onFilterChange as (filters: unknown) => void}
      />
    </FullscreenDataGridWrapper>
  );
};
